import React, { useState } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { InternalApiAlertNoteTemplate, InternalApiAlertType } from '../../api-internal';
import { SalvButton, SalvDropdown, SalvModal, SalvTextArea, SalvTextField, Size } from '@salvapp/ui-components';
import {
  CAN_ADD_ALERT_NOTE_TEMPLATE,
  CAN_DELETE_ALERT_NOTE_TEMPLATE,
  CAN_MODIFY_ALERT_NOTE_TEMPLATE,
} from '../../auth/permissions';
import Has from '../../auth/Has';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { alertNoteTemplateInternalApi } from '../../apis';
import { useDispatch } from 'react-redux';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import './AlertNoteTemplateEditor.scss';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { useMe } from '../../organisation/useMe';

interface AlertNoteTemplateEditorProps {
  template?: InternalApiAlertNoteTemplate | null;
  defaultAlertType?: InternalApiAlertType;
  onClose: () => void;
  onSave: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required')
    .min(1, 'Name must be at least 1 character')
    .max(255, 'Name must be less than 255 characters'),
  alertType: Yup.string()
    .oneOf(Object.values(InternalApiAlertType), 'Invalid alert type')
    .required('Alert type is required'),
  content: Yup.string().required('Content is required').min(1, 'Content must be at least 1 character'),
});

const AlertNoteTemplateEditor: React.FC<AlertNoteTemplateEditorProps> = ({
  template,
  defaultAlertType,
  onClose,
  onSave,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { features } = useMe();

  const isBridgeEnabled = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE, features);

  const isEditing = !!template;

  const onDeleteTemplate = async () => {
    if (!template?.id) return;

    dispatch(
      showConfirmationModal({
        title: 'Delete Note Template?',
        description: `"${template.name}" will be permanently deleted and cannot be recovered.`,
        confirmationText: 'Yes, delete it',
        cancelText: 'Cancel',
        onConfirm: async () => {
          try {
            await alertNoteTemplateInternalApi.deleteAlertNoteTemplate({
              id: template.id!,
            });
            onSave(); // Triggers parent to refresh list and close modal
          } catch (error) {
            console.error('Error deleting template:', error);
            // Error is already handled by apiErrorLoggerMiddleware
          }
        },
      })
    );
  };

  const formik = useFormik({
    initialValues: {
      name: template?.name || '',
      alertType: template?.alertType || defaultAlertType || InternalApiAlertType.MONITORING,
      content: template?.content || '',
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        if (isEditing && template) {
          await alertNoteTemplateInternalApi.updateAlertNoteTemplate({
            id: template.id!,
            internalApiUpdateAlertNoteTemplateRequest: {
              name: values.name,
              alertType: values.alertType as InternalApiAlertType,
              content: values.content,
            },
          });
        } else {
          await alertNoteTemplateInternalApi.createAlertNoteTemplate({
            internalApiCreateAlertNoteTemplateRequest: {
              name: values.name,
              alertType: values.alertType as InternalApiAlertType,
              content: values.content,
            },
          });
        }
        onSave();
      } catch (error) {
        console.error('Error saving template:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const alertTypeOptions: DropdownOption[] = [
    ...(isBridgeEnabled
      ? [{ key: InternalApiAlertType.BRIDGE, value: InternalApiAlertType.BRIDGE, label: 'Bridge' }]
      : []),
    { key: InternalApiAlertType.MANUAL, value: InternalApiAlertType.MANUAL, label: 'Manual' },
    { key: InternalApiAlertType.MONITORING, value: InternalApiAlertType.MONITORING, label: 'Monitoring' },
    { key: InternalApiAlertType.SCREENING, value: InternalApiAlertType.SCREENING, label: 'Screening' },
  ];

  const getModalTitle = () => {
    if (isEditing) return 'Edit Note Template';
    return 'Create Note Template';
  };

  return (
    <SalvModal show={true} onHide={onClose} className='alert-note-template-editor-modal' allowDismissal={false}>
      <FormikProvider value={formik}>
        <Form>
          <SalvModal.Header>{getModalTitle()}</SalvModal.Header>

          <SalvModal.Body>
            <div className='alert-note-template-editor-container'>
              <div className='form-section'>
                <div className='form-group'>
                  <SalvTextField
                    label='Template Name'
                    value={formik.values.name}
                    onChange={(e) => formik.setFieldValue('name', e)}
                    onBlur={formik.handleBlur}
                    placeholder='Enter template name'
                    required
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className='invalid-feedback d-block'>{formik.errors.name}</div>
                  )}
                </div>

                <div className='form-group'>
                  <SalvDropdown
                    label='Alert Type'
                    value={alertTypeOptions.find((opt) => opt.value === formik.values.alertType) || alertTypeOptions[0]}
                    onChange={([option]) => formik.setFieldValue('alertType', option?.value)}
                    options={alertTypeOptions}
                    required
                  />
                  {formik.touched.alertType && formik.errors.alertType && (
                    <div className='invalid-feedback d-block'>{formik.errors.alertType}</div>
                  )}
                </div>

                <div className='form-group'>
                  <SalvTextArea
                    label='Template Content'
                    value={formik.values.content}
                    onChange={(e) => formik.setFieldValue('content', e)}
                    onBlur={formik.handleBlur}
                    placeholder='Enter template content. You can use this template when creating alert notes.'
                    autoSizing
                    minRows={8}
                    required
                  />
                  {formik.touched.content && formik.errors.content && (
                    <div className='invalid-feedback d-block'>{formik.errors.content}</div>
                  )}
                  <div className='form-text'>
                    This content will be used as a template for alert notes of type{' '}
                    {alertTypeOptions.find((opt) => opt.value === formik.values.alertType)?.label ||
                      alertTypeOptions[0].label}
                    .
                  </div>
                </div>
              </div>
            </div>
          </SalvModal.Body>

          <SalvModal.Footer>
            {isEditing && template && (
              <Has allPermissions={CAN_DELETE_ALERT_NOTE_TEMPLATE}>
                <SalvButton
                  type='button'
                  onClick={onDeleteTemplate}
                  disabled={isSubmitting}
                  variant='danger'
                  size={Size.LARGE}
                  label='Delete'
                  className='me-auto'
                />
              </Has>
            )}
            <SalvButton
              type='button'
              variant='muted'
              onClick={onClose}
              disabled={isSubmitting}
              size={Size.LARGE}
              label='Cancel'
            />
            <Has allPermissions={isEditing ? CAN_MODIFY_ALERT_NOTE_TEMPLATE : CAN_ADD_ALERT_NOTE_TEMPLATE}>
              <SalvButton
                type='submit'
                disabled={isSubmitting || !formik.isValid || !formik.dirty}
                variant='primary'
                size={Size.LARGE}
                label={isEditing ? 'Update Template' : 'Create Template'}
              />
            </Has>
          </SalvModal.Footer>
        </Form>
      </FormikProvider>
    </SalvModal>
  );
};

export default AlertNoteTemplateEditor;
